import { Link } from "wouter";
import { useState, useEffect, useRef } from "react";
import { applySeo } from "@/lib/seo";
import { CheckoutButton } from "@/components/CheckoutButton";
import { AnalysisPreviewCard, PREVIEW_DATA } from "@/components/AnalysisPreviewCard";
import { BAROMETER_PRICES, PAKET_PRICES, WEBSITE_EINZELSUMME, WEBSITE_REALITAETSCHECK_MIT_VIDEOCALL, VIDEOCALL_1H_AUFPREIS, formatPrice } from "@shared/prices";

const LOGO_URL = "/manus-storage/logo_2d439bd1.jpg";
const PORTRAIT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663476281654/fDRgangGWFphFKzWtNAEav/larissa-portrait_abbef783.jpg";

const C = {
  accent: "#115751",      // Teal – technisch, vertrauenswürdig
  accentDark: "#0a3d39",
  accentLight: "#11575112",
  bordeaux: "#6d0000",
  bg: "#ffffff",
  bgGray: "#f2f2f2",
  text: "#000000",
  muted: "#6b7280",
  border: "#e5e7eb",
};

// Animierter Score-Counter
function AnimatedScore({ target, duration = 1800 }: { target: number; duration?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <span ref={ref}>{count}</span>;
}

// Animierte Balken-Komponente
function DemoBar({ label, score, max, color, delay }: { label: string; score: number; max: number; color: string; delay: number }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth((score / max) * 100), delay);
    return () => clearTimeout(t);
  }, [score, max, delay]);
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <span className="text-gray-700">{label}</span>
        <span className="font-bold" style={{ color }}>{score}/{max}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${width}%`, backgroundColor: color, transitionDuration: "1.2s", transitionTimingFunction: "ease-out" }}
        />
      </div>
    </div>
  );
}

const INCLUDED_BAROMETER = [
  {
    id: "seo",
    icon: "/manus-storage/23_1ab7147e.svg",
    name: "SEO-Barometer™",
    price: BAROMETER_PRICES.seo,
    color: "#0a5c36",
    path: "/seo-barometer",
    desc: "SEO-Grundlagen-Check: On-Page SEO, technische SEO, Core Web Vitals (Google PageSpeed), E-E-A-T – kein Backlink-Check",
    dimensions: [
      { label: "On-Page Grundlagen", score: 18, max: 25 },
      { label: "Technische SEO", score: 14, max: 20 },
      { label: "Ladegeschwindigkeit", score: 12, max: 20 },
      { label: "Inhaltsqualität", score: 16, max: 25 },
      { label: "E-E-A-T Signale", score: 7, max: 10 },
    ],
    sampleScore: 67,
    sampleGrade: "B",
  },
  {
    id: "lokal",
    icon: "/manus-storage/1_55991aa3.svg",
    name: "Lokal-Barometer™",
    price: BAROMETER_PRICES.lokal,
    color: "#115751",
    path: "/lokal-barometer",
    desc: "Google Business Profile, lokale Sichtbarkeit, NAP-Konsistenz, Bewertungen, Maps",
    dimensions: [
      { label: "Google Business Profile", score: 15, max: 20 },
      { label: "NAP-Konsistenz", score: 9, max: 15 },
      { label: "Lokale Keywords", score: 12, max: 20 },
      { label: "Bewertungen", score: 8, max: 15 },
      { label: "Lokale Backlinks", score: 7, max: 10 },
    ],
    sampleScore: 51,
    sampleGrade: "C",
  },
  {
    id: "accessibility",
    icon: "/manus-storage/16_9c3f7718.svg",
    name: "Accessibility-Barometer™",
    price: BAROMETER_PRICES.accessibility,
    color: "#4338ca",
    path: "/accessibility-barometer",
    desc: "WCAG 2.2-Konformität, ARIA-Rollen, Tastaturnavigation, European Accessibility Act",
    dimensions: [
      { label: "Wahrnehmbarkeit", score: 17, max: 25 },
      { label: "Bedienbarkeit", score: 14, max: 20 },
      { label: "Verständlichkeit", score: 13, max: 20 },
      { label: "Robustheit", score: 8, max: 15 },
      { label: "EAA-Compliance", score: 14, max: 20 },
    ],
    sampleScore: 66,
    sampleGrade: "B",
  },
  {
    id: "datenschutz",
    icon: "/manus-storage/29_7f3bb394.svg",
    name: "Datenschutz-Barometer™",
    price: BAROMETER_PRICES.datenschutz,
    color: "#0e7490",
    path: "/datenschutz-barometer",
    desc: "DSGVO-Konformität, Cookie-Consent, Impressum, SSL, externe Dienste, Datenschutzerklärung",
    dimensions: [
      { label: "Cookie-Consent", score: 14, max: 20 },
      { label: "Impressum & Pflichtangaben", score: 12, max: 20 },
      { label: "Datenschutzerklärung", score: 11, max: 20 },
      { label: "SSL & Sicherheit", score: 16, max: 20 },
      { label: "Externe Dienste", score: 12, max: 20 },
    ],
    sampleScore: 65,
    sampleGrade: "B",
  },
];

const SINGLE_TOTAL = INCLUDED_BAROMETER.reduce((s, b) => s + b.price, 0);
const BUNDLE_PRICE = PAKET_PRICES.website;
const SAVINGS = SINGLE_TOTAL - BUNDLE_PRICE;

export default function WebsiteRealitaetscheck() {
  useEffect(() => applySeo({
    title: "Website Realitäts-Check™ – Was kostet dich deine Website wirklich? | WERBEAGENTUR Larissa Girenko",
    description: "SEO, Sensorik, Conversion, Accessibility & Datenschutz in einer Vollanalyse. Website Realitäts-Check™ zeigt den echten Status deiner Website.",
    path: "/website-realitaetscheck",
  }), []);
  const [activeDemo, setActiveDemo] = useState(0);
  const [demoKey, setDemoKey] = useState(0);

  function switchDemo(idx: number) {
    setActiveDemo(idx);
    setDemoKey(k => k + 1);
  }

  const demo = INCLUDED_BAROMETER[activeDemo];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Navigation */}
      <header className="sticky top-0 z-50 bg-white border-b" style={{ borderColor: C.border }}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link href="/">
            <div className="flex items-center gap-3 cursor-pointer">
              <img src={LOGO_URL} alt="Werbeagentur Larissa Girenko" className="w-10 h-10 object-contain" />
              <div>
                <div className="font-black uppercase text-sm tracking-widest" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.text }}>
                  Werbeagentur Larissa Girenko
                </div>
                <div className="text-xs" style={{ color: C.muted }}>Marketing Barometer Suite</div>
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link href="/">
              <button className="text-xs font-bold tracking-widest uppercase px-3 py-1.5 border" style={{ borderColor: C.border, color: C.muted, fontFamily: "'DM Sans', sans-serif" }}>
                ← Übersicht
              </button>
            </Link>
            <Link href="/marketing-realitaetscheck">
              <button className="text-xs font-bold tracking-widest uppercase px-3 py-1.5" style={{ backgroundColor: C.bordeaux, color: "#fff", fontFamily: "'DM Sans', sans-serif" }}>
                Marketing Check →
              </button>
            </Link>
          </div>
        </div>
      </header>

      {/* CI-Akzentlinie */}
      <div className="h-1 w-full" style={{ backgroundColor: C.accent }} />

      {/* Hero */}
      <section className="bg-black text-white py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(ellipse at 70% 50%, ${C.accent} 0%, transparent 60%)` }} />
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-10">
            <div className="md:w-[55%]">
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 text-xs font-bold tracking-widest uppercase border" style={{ borderColor: C.accent, color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>
                ● Bundle-Paket
              </div>
              <h1 className="font-black uppercase leading-none mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2.8rem, 6vw, 5rem)" }}>
                Website<br />
                <span style={{ color: C.accent }}>Realitäts-Check™</span>
              </h1>
              <p className="text-gray-300 text-base leading-relaxed mb-6 max-w-lg" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Ich analysiere deine Website auf die vier wichtigsten technischen und rechtlichen Dimensionen: SEO, lokale Sichtbarkeit, Barrierefreiheit und DSGVO-Konformität – in einem einzigen Bericht.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {INCLUDED_BAROMETER.map(b => (
                  <span key={b.id} className="text-xs font-bold px-3 py-1 rounded-full" style={{ backgroundColor: `${b.color}22`, color: b.color, fontFamily: "'DM Sans', sans-serif" }}>
                    <><img src={b.icon} alt="" className="inline-block w-4 h-4 mr-1 object-contain align-middle" style={{ opacity: 0.85 }} />{b.name.replace("™", "").replace("-Barometer", "")}</>
                  </span>
                ))}
              </div>
              <div className="flex items-end gap-4">
                <div>
                  <div className="font-black text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(3rem, 6vw, 4.5rem)", lineHeight: 1 }}>
                    {BUNDLE_PRICE} €
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1 mb-2">
                    <span className="text-xs text-gray-500 line-through">{SINGLE_TOTAL} €</span>
                    <span className="text-xs font-bold px-2 py-0.5" style={{ backgroundColor: "#16a34a", color: "white" }}>−{SAVINGS} € gespart</span>
                  </div>
                  <div className="text-xs font-bold mb-2" style={{ color: C.accent }}>= {(BUNDLE_PRICE / 4).toFixed(0)} € pro Barometer</div>
                  <p className="text-xs text-yellow-500 font-bold mb-3">⏳ Aktionspreis – begrenzte Zeit</p>
                </div>
                <CheckoutButton
                  productId="website_realitaetscheck"
                  label="Jetzt starten"
                  accentColor={C.accent}
                  returnPath="/website-realitaetscheck"
                />
              </div>
            </div>

            {/* Preis-Karte */}
            <div className="md:w-[40%] border border-gray-700 p-6" style={{ background: "rgba(255,255,255,0.04)" }}>
              <div className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: C.accent }}>Enthaltene Analysen</div>
              {INCLUDED_BAROMETER.map(b => (
                <div key={b.id} className="flex items-center justify-between py-3 border-b border-gray-800">
                  <div className="flex items-center gap-2">
                    <img src={b.icon} alt="" className="w-5 h-5 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                    <span className="text-sm text-white font-medium">{b.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 line-through">{b.price} €</span>
                    <span className="text-xs font-bold" style={{ color: C.accent }}>✓</span>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-4">
                <span className="text-sm font-bold text-white">Gesamt</span>
                <div className="text-right">
                  <div className="text-xs text-gray-500 line-through">{SINGLE_TOTAL} €</div>
                  <div className="font-black text-white text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{BUNDLE_PRICE} €</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo-Ausschnitt */}
      <section className="py-16 px-4" style={{ backgroundColor: C.bgGray }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Live-Vorschau</div>
            <h2 className="font-black uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)" }}>
              So sieht deine Analyse aus
            </h2>
            <p className="text-gray-500 text-sm mt-2 max-w-lg mx-auto">Wähle einen Barometer und sieh, wie deine Ergebnisse aussehen werden</p>
          </div>

          {/* Tab-Auswahl */}
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {INCLUDED_BAROMETER.map((b, i) => (
              <button
                key={b.id}
                onClick={() => switchDemo(i)}
                className="px-4 py-2 text-xs font-bold tracking-widest uppercase transition-all"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  backgroundColor: activeDemo === i ? b.color : "transparent",
                  color: activeDemo === i ? "#fff" : C.muted,
                  border: `1px solid ${activeDemo === i ? b.color : C.border}`,
                }}
              >
                <><img src={b.icon} alt="" className="inline-block w-4 h-4 mr-1 object-contain align-middle" style={{ opacity: 0.85 }} />{b.name.replace("™", "").replace("-Barometer", "")}</>
              </button>
            ))}
          </div>

          {/* Demo-Fenster */}
          <div key={demoKey} className="bg-white border shadow-sm overflow-hidden" style={{ borderColor: C.border }}>
            {/* Browser-Leiste */}
            <div className="flex items-center gap-2 px-4 py-2 border-b bg-gray-50" style={{ borderColor: C.border }}>
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              <div className="flex-1 bg-white border rounded text-xs px-3 py-1 text-gray-400" style={{ borderColor: C.border }}>
                barometer.werbeagentur-girenko.de{demo.path}
              </div>
            </div>

            <div className="p-6">
              {/* Score-Header */}
              <div className="flex flex-col md:flex-row md:items-center gap-6 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center border-4 flex-shrink-0"
                    style={{ borderColor: demo.color, background: `${demo.color}12` }}>
                    <div>
                      <div className="font-black text-center leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.8rem", color: demo.color }}>
                        {demo.sampleGrade}
                      </div>
                      <div className="text-xs text-center text-gray-500">
                        <AnimatedScore target={demo.sampleScore} />%
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="font-black uppercase text-lg" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{demo.name}</div>
                    <div className="text-xs text-gray-500">Beispiel-Analyse · example.de</div>
                  </div>
                </div>
                <div className="flex-1 md:text-right">
                  <div className="inline-flex items-center gap-2 px-3 py-1 text-xs font-bold" style={{ backgroundColor: `${demo.color}15`, color: demo.color }}>
                    ✓ Analyse abgeschlossen
                  </div>
                </div>
              </div>

              {/* Dimensionen-Balken */}
              <div className="grid md:grid-cols-2 gap-x-10">
                <div>
                  <div className="text-xs font-bold tracking-widest uppercase mb-4 text-gray-400">Dimensionen-Übersicht</div>
                  {demo.dimensions.map((d, i) => (
                    <DemoBar key={`${demoKey}-${i}`} label={d.label} score={d.score} max={d.max} color={demo.color} delay={i * 200} />
                  ))}
                </div>
                <div>
                  <div className="text-xs font-bold tracking-widest uppercase mb-4 text-gray-400">Top-Empfehlungen</div>
                  {[
                    "Meta-Beschreibungen für alle Seiten optimieren",
                    "Ladezeit unter 2 Sekunden reduzieren",
                    "Alt-Texte für alle Bilder ergänzen",
                  ].map((rec, i) => (
                    <div key={i} className="flex items-start gap-2 mb-3 p-3 border-l-2" style={{ borderColor: demo.color, backgroundColor: `${demo.color}08` }}>
                      <span className="text-xs font-bold mt-0.5" style={{ color: demo.color }}>→</span>
                      <span className="text-xs text-gray-700">{rec}</span>
                    </div>
                  ))}
                  <div className="mt-4 p-3 border text-center" style={{ borderColor: demo.color }}>
                    <div className="text-xs text-gray-500 mb-1">+ 17 weitere Empfehlungen im Vollreport</div>
                    <div className="text-xs font-bold" style={{ color: demo.color }}>Inkl. PDF-Export</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Beispiel-Ausschnitte */}
      <section className="py-16 px-4 border-t border-gray-200">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Beispiel-Ergebnisse</div>
            <h2 className="font-black uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)" }}>
              So sehen die 4 Analysen aus
            </h2>
            <p className="text-gray-500 text-sm mt-2 max-w-lg mx-auto">Echte Scores, Dimensionen und der wichtigste Optimierungspunkt – der vollständige Report enthält alle Details.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {PREVIEW_DATA.filter(d => ["/manus-storage/23_1ab7147e.svg", "/manus-storage/1_55991aa3.svg", "/manus-storage/16_9c3f7718.svg", "/manus-storage/29_7f3bb394.svg"].includes(d.icon)).map(d => (
              <AnalysisPreviewCard key={d.title} data={d} />
            ))}
          </div>
        </div>
      </section>

      {/* Was du bekommst */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Leistungsumfang</div>
            <h2 className="font-black uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)" }}>
              Was du bekommst
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {INCLUDED_BAROMETER.map(b => (
              <div key={b.id} className="border p-6" style={{ borderColor: C.border, borderLeft: `4px solid ${b.color}` }}>
                <div className="flex items-center gap-3 mb-3">
                  <img src={b.icon} alt="" className="w-7 h-7 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                  <div>
                    <div className="font-black uppercase text-sm" style={{ fontFamily: "'Bebas Neue', sans-serif", color: b.color }}>{b.name}</div>
                    <div className="text-xs text-gray-500">Einzelpreis: {b.price} €</div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{b.desc}</p>
                <Link href={b.path}>
                  <button className="mt-4 text-xs font-bold tracking-widest uppercase" style={{ color: b.color, fontFamily: "'DM Sans', sans-serif" }}>
                    Mehr erfahren →
                  </button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Preisvergleich */}
      <section className="py-16 px-4" style={{ backgroundColor: "#000" }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Warum {BUNDLE_PRICE} €?</div>
          <h2 className="font-black uppercase text-white mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)" }}>
            Was andere dafür verlangen
          </h2>
          <div className="grid md:grid-cols-3 gap-4 mb-10">
            {[
              { label: "SEO-Agentur", price: "600–4.000 €", unit: "/ Monat", note: "Laufende Optimierung" },
              { label: "DSGVO-Beratung", price: "200–800 €", unit: "/ Einmalig", note: "Rechtliche Prüfung" },
              { label: "WCAG-Audit", price: "500–2.000 €", unit: "/ Einmalig", note: "Barrierefreiheitsprüfung" },
            ].map((item, i) => (
              <div key={i} className="border border-gray-700 p-5 text-left">
                <div className="text-xs text-gray-500 mb-1">{item.label}</div>
                <div className="font-black text-white text-xl mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{item.price}</div>
                <div className="text-xs text-gray-400">{item.unit} · {item.note}</div>
              </div>
            ))}
          </div>
          <div className="border border-gray-600 p-8 inline-block">
            <div className="text-xs text-gray-400 mb-2">Basic Website-RealitätsCheck™</div>
            <div className="font-black text-white mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(3rem, 6vw, 4.5rem)", lineHeight: 1 }}>
              {BUNDLE_PRICE} €
            </div>
            <div className="text-sm text-gray-300 mb-6">Alle 4 Analysen · Sofortergebnis · PDF-Export</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                  <div className="border border-gray-600 p-4 flex flex-col">
                    <p className="text-xs font-bold tracking-widest uppercase mb-1 text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Option 1</p>
                    <h4 className="font-black uppercase text-base mb-1 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Vollständige Analyse</h4>
                    <p className="text-gray-400 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>4 Analysen, PDF-Report, Lieferung in 72h.</p>
                    <div className="text-2xl font-black text-white mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{BUNDLE_PRICE} €</div>
                    <CheckoutButton productId="website_realitaetscheck" label="Jetzt buchen" accentColor={C.accent} returnPath="/website-realitaetscheck" fullWidth />
                  </div>
                  <div className="border-2 p-4 flex flex-col relative" style={{ borderColor: C.accent }}>
                    <div className="absolute -top-3 left-3 px-2 py-0.5 text-white text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Empfohlen</div>
                    <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Option 2</p>
                    <h4 className="font-black uppercase text-base mb-1 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Analyse + VideoCall</h4>
                    <p className="text-gray-400 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>4 Analysen + 1h persönliche Ergebnis-Interpretation mit Larissa per VideoCall.</p>
                    <div className="text-2xl font-black mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.accent }}>{formatPrice(WEBSITE_REALITAETSCHECK_MIT_VIDEOCALL)}</div>
                    <p className="text-xs text-gray-500 mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>inkl. 1h-VideoCall (+{VIDEOCALL_1H_AUFPREIS} €)</p>
                    <CheckoutButton productId="website_realitaetscheck_mit_videocall" label="Analyse + VideoCall" accentColor={C.accent} returnPath="/website-realitaetscheck" fullWidth />
                  </div>
                </div>
          </div>
        </div>
      </section>

      {/* Larissa-Sektion */}
      <section className="py-16 px-4" style={{ backgroundColor: C.bgGray }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="relative overflow-hidden" style={{ maxHeight: "480px" }}>
                <img
                  src={PORTRAIT_IMG}
                  alt="Larissa Girenko – Expertin für Website-Analyse"
                  className="w-full object-cover object-top"
                  style={{ maxHeight: "480px" }}
                />
              </div>
            </div>
            <div>
              <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Über die Entwicklerin</p>
              <h2 className="font-black uppercase leading-none mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 4vw, 3rem)" }}>
                Larissa Girenko
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Ich bin Gründerin der Werbeagentur Larissa Girenko und Entwicklerin der Marketing Barometer Suite. Der Website Realitäts-Check entstand aus der Erkenntnis, dass die meisten Websites gleichzeitig an vier kritischen Punkten scheitern: schlechte SEO, fehlende lokale Sichtbarkeit, DSGVO-Verstöße und Barrierefreiheitsprobleme. Ich habe diese Analysen in einem Paket gebündelt, damit du nicht vier separate Tools brauchst.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { value: "4", label: "Analysen in einem" },
                  { value: formatPrice(PAKET_PRICES.website), label: `statt ${formatPrice(WEBSITE_EINZELSUMME)}` },
                  { value: "60 Sek.", label: "bis zum Ergebnis" },
                  { value: "PDF", label: "Export inklusive" },
                ].map((stat, i) => (
                  <div key={i} className="border-l-2 pl-3" style={{ borderColor: C.accent }}>
                    <div className="font-black text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.accent }}>{stat.value}</div>
                    <div className="text-xs text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t" style={{ borderColor: C.border }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="w-8 h-8 object-contain" />
            <span className="text-xs text-gray-500">© 2026 Werbeagentur Larissa Girenko</span>
          </div>
          <div className="flex gap-6 text-xs text-gray-400">
            <Link href="/impressum"><span className="hover:text-gray-600 cursor-pointer">Impressum</span></Link>
            <Link href="/datenschutz"><span className="hover:text-gray-600 cursor-pointer">Datenschutz</span></Link>
            <Link href="/marketing-realitaetscheck"><span className="cursor-pointer font-bold" style={{ color: C.bordeaux }}>Marketing Realitäts-Check™ →</span></Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
